import {useState, useEffect, useCallback} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import Collapse from '@mui/material/Collapse';

import {usePathname} from 'src/routes/hooks';
import {useActiveLink} from 'src/routes/hooks/use-active-link';

import NavItem from './nav-item';
import {NavListProps, NavSubListProps} from '../types';
import {
  BASE_MENU_ITEM,
  BASE_ROUTE,
  BASE_ROUTE_MODULE
} from 'src/routes/BaseRoute';
import {createAuditLog} from '@shared/services/service-handlers/PrivateService';
import AuthUtils from 'src/utils/AuthUtils';
import {useAppContext} from '@context/AppContext';

// ----------------------------------------------------------------------

export default function NavList({
  data,
  depth,
  slotProps,
  url,
  subListPath,
  moduleName,
  setExternalUrl
}: NavListProps) {
  const pathname = usePathname();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    appState: {applicationName}
  } = useAppContext();

  const isActive =
    location.pathname?.replace(/\/$/, '') ===
      `/${BASE_ROUTE}/${BASE_ROUTE_MODULE}/${subListPath}` &&
    location.search === `?${BASE_MENU_ITEM}=${url}`;

  const activeLink = useActiveLink(
    `/${BASE_ROUTE}/${BASE_ROUTE_MODULE}/` + data.url,
    !!data?.listMenuItems && data?.listMenuItems?.length > 0
  );

  if (activeLink) {
    let eventDescription = `User accessed module : ${data.url}`;

    if (location.search?.length) {
      eventDescription += `, User accessed sub list: ${location.search.replace('?menu_item=', '')}`;
    }
    const dataObj = {
      isBH_selected: true,
      isBH_reason: '',
      application_name: applicationName,
      event_description: eventDescription,
      audit_identifier: AuthUtils.getAuditIdentifier(),
      audit_identifier_type: AuthUtils.getAuditIdentifierType(),
      audit_legacy_app: AuthUtils.getAuditLegacyApp()
    };
    createAuditLog(data.url, dataObj);
  }
  const active = subListPath && url ? isActive : activeLink;

  const [openMenu, setOpenMenu] = useState(active);

  const handleCloseMenu = useCallback(() => {
    setOpenMenu(false);
  }, []);

  useEffect(() => {
    if (!active) {
      handleCloseMenu();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const handleToggleMenu = useCallback(() => {
    if (data.listMenuItems && data?.listMenuItems?.length > 0) {
      setOpenMenu(prev => !prev);
      const subPath =
        `/${BASE_ROUTE}/${BASE_ROUTE_MODULE}/` +
        data.url +
        (data?.listMenuItems?.[0]?.url?.length
          ? `/?${BASE_MENU_ITEM}=${data?.listMenuItems?.[0]?.url}`
          : '');
      navigate(subPath);
    }
  }, [data.listMenuItems]);

  return (
    <>
      <NavItem
        open={openMenu}
        onClick={handleToggleMenu}
        //
        name={data.name}
        moduleName={moduleName}
        url={
          url
            ? `?${BASE_MENU_ITEM}=${url}`
            : `/${BASE_ROUTE}/${BASE_ROUTE_MODULE}/` + data.url
        }
        icon={data.icon}
        info={data.info}
        roles={data.roles}
        caption={data.caption}
        disabled={data.disabled}
        //
        depth={depth}
        hasChild={data.hasListMenuItems}
        externalLink={data.url.includes('http')}
        setExternalUrl={setExternalUrl}
        iframe={data.external_link}
        currentRole={slotProps?.currentRole}
        //
        active={active}
        className={active ? 'active' : ''}
        sx={{
          mb: `${slotProps?.gap}px`,
          ...(depth === 1 ? slotProps?.rootItem : slotProps?.subItem)
        }}
        path={''}
      />

      {!!data?.listMenuItems && data?.listMenuItems?.length > 0 && (
        <Collapse in={openMenu} unmountOnExit>
          <NavSubList
            data={data.listMenuItems}
            depth={depth}
            slotProps={slotProps}
            url={data.url}
          />
        </Collapse>
      )}
    </>
  );
}

// ----------------------------------------------------------------------

function NavSubList({data, depth, slotProps, url}: NavSubListProps) {
  return (
    <>
      {data.map(list => (
        <NavList
          key={list.name}
          data={list}
          depth={depth + 1}
          slotProps={slotProps}
          url={list.url}
          subListPath={url}
        />
      ))}
    </>
  );
}
